<template>
	<div>
		<div class="tw-text-3xl tw-font-bold" v-if="!isDone">
			{{ $t('integrations.authorize-title') }}
		</div>
		<div class="tw-flex tw-items-center tw-gap-2" v-else>
			<div class="tw-text-3xl tw-font-bold">{{ $t('integrations.store-authorized') }}</div>
			<div
				class="'tw-border-secondary-500 tw-z-10 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-bg-secondary-500 tw-text-white"
				style="width: 32px; height: 32px"
			>
				<AppIcon name="check" />
			</div>
		</div>
		<div class="tw-font-medium tw-text-gray-500">
			{{ $t('integrations.authorize-description', { marketplace: marketplaceName }) }}
		</div>
		<AppForm
			@submit="onSubmit"
			class="tw-mt-3 tw-flex tw-flex-col tw-gap-4"
			style="max-width: 70%"
		>
			<template v-if="!isDone">
				<AppInput
					v-if="showDomain"
					v-model="domain"
					:placeholder="$t('integrations.your-store-domain')"
					:hint="$t('integrations.store-domain-hint')"
					:rules="['required']"
					:disabled="loading || isDone"
					class="domain-input"
				>
					<template #end-btn>
						<div
							class="tw-flex tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-bg-gray-50 tw-p-2 tw-text-gray-600"
							style="height: 44px"
						>
							.myshopify.com
						</div>
					</template>
				</AppInput>

				<AppCheckbox v-model="autoSync" v-if="showAutoSync">
					<template v-slot:label="{ toggle }">
						<div class="tw-flex tw-cursor-pointer tw-flex-col tw-ps-3" @click="toggle()">
							<div class="tw-text-lg tw-font-medium tw-text-gray-700">
								{{
									$t('integrations.activate-auto-sync-brands-categories-products-orders')
								}}
							</div>
							<div class="tw-text-lg tw-text-gray-600">
								{{
									$t('integrations.select-to-sync-marketplace', {
										marketplace: marketplaceName,
									})
								}}
							</div>
						</div>
					</template>
				</AppCheckbox>
				<div class="tw-mt-5">
					<AppButton
						:label="loading ? $t('integrations.authorizing') : $t('integrations.authorize')"
						size="xl"
						class="tw-rounded-full"
						type="submit"
						style="width: 250px"
						:disabled="loading"
					/>
				</div>
			</template>
			<div class="tw-mt-5 tw-flex tw-items-center tw-gap-3" v-else>
				<AppButton
					:label="$t('integrations.cancel-authorization')"
					size="xl"
					class="tw-rounded-full"
					type="button"
					secondary
					variant="gray"
					@click="cancelAuth"
				/>
				<AppButton
					:label="$t('integrations.next-step')"
					size="xl"
					class="tw-rounded-full"
					type="button"
					endIcon="assets/v2/image/icons/arrow-narrow-right.svg"
					@click="nextStep"
					:disabled="nextStepLoading"
				/>
			</div>
		</AppForm>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useLinkMarketplaceStore } from '../../../../stores/link-marketplace';
import { marketplacesEnum } from '@/v2/utils/marketplaces';
import { Marketplace } from '../../../../entities/marketplace';
import { marketplaceStepStatusEnum } from '../../helper';

const linkMarketplaceStore = useLinkMarketplaceStore();

const domain = ref('');
const autoSync = ref(true);

const loading = ref(linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.InProgress);
const isDone = ref(
	linkMarketplaceStore.currentStepData.status === marketplaceStepStatusEnum.Complete,
);
const nextStepLoading = ref(false);

const marketplaceName = computed(() => {
	return linkMarketplaceStore.marketplace.name || '';
});

const showDomain = computed(() => {
	return [marketplacesEnum.Shopify].includes(linkMarketplaceStore.marketplace.id);
});

const showAutoSync = computed(() => {
	return [marketplacesEnum.Shopify, marketplacesEnum.Zid, marketplacesEnum.Amazon].includes(
		linkMarketplaceStore.marketplace.id,
	);
});

const onSubmit = async () => {
	loading.value = true;
	try {
		const { redirectUrl, success } = await Marketplace.authorizeStore(
			linkMarketplaceStore.linkedMarketplaceId,
			{
				domain: showDomain.value ? domain.value || undefined : undefined,
				auto_integrate: showAutoSync.value ? autoSync.value : undefined,
			},
		);
		if (!success) {
			document.location.href = '/store/subscribe/renew_package';
			return;
		}
		window.location.href = redirectUrl;
	} catch (err) {
		loading.value = false;
	}
};

const cancelAuth = () => {
	isDone.value = false;
};
const nextStep = async () => {
	nextStepLoading.value = true;
	try {
		await Marketplace.finishWizardStep(linkMarketplaceStore.currentStepData.id);
		await linkMarketplaceStore.refetchSteps();
	} finally {
		nextStepLoading.value = false;
	}
};
</script>

<style lang="scss" scoped>
.domain-input {
	::v-deep(.input-wrapper) {
		direction: ltr;
	}
}
</style>
